import { useState, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import _pick from 'lodash/pick'
import _isArray from 'lodash/isArray'
import HeaderFilterButton from 'app/ProviderSearch/HeaderFilterButton'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import styles from './index.module.scss'
import { filterTitle, sessionStorageKeys } from 'constants/common'
import PsychNoticeModal from 'app/ProviderSearch/PsychNoticeModal'
import { CounselingTypeEnum } from 'app/Bookings/constants'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useThriveAuth from 'hooks/useThriveAuth'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import useStimulantBucket from 'hooks/bookings/useStimulantBucket'
import { useAllFilters } from 'context/ProviderSearch/AllFiltersProvider'
import { withUnavailableSelectedFilters } from 'app/ProviderSearch/utils'
const MultiselectDropdown = dynamic(() => import('common/MultiselectDropdown'))

type AreaOfConcernFilterProps = {
  onFilterSelect: (selectedFilter: Record<string, unknown>) => void
  groupedFilters: GroupedProviderFilter
  containerClassName?: string
  isAdhdFilterSelected?: boolean
}

const AreaOfConcernFilter: React.FC<AreaOfConcernFilterProps> = ({
  onFilterSelect,
  groupedFilters,
  containerClassName = '',
  isAdhdFilterSelected = false,
}) => {
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const router = useRouter()
  const { query } = router
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [showPsychNoticeModal, setShowPsychNoticeModal] = useState<boolean>(
    false
  )
  const [appliedAreasOfConcern, setAppliedAreasOfConcerns] = useState<string[]>(
    []
  )

  const allGroupedFilters = useAllFilters()
  const allAreasOfConcern = withUnavailableSelectedFilters(
    groupedFilters[ProviderFilterTypes.AREA_OF_CONCERN],
    allGroupedFilters?.[ProviderFilterTypes.AREA_OF_CONCERN],
    appliedAreasOfConcern
  )

  const showPsychModalTreatment = useGetTreatment(
    TreatmentFlags.COE_PYSCH_MODAL,
    true,
    {
      support: isSupportOrThirdPartyUser,
    }
  )

  const isShowPsychModalTreatmentON =
    showPsychModalTreatment === treatmentFlagValues.SHOW

  const currentState =
    typeof query?.state === 'string' ? query.state.trim().toUpperCase() : ''
  const { bucket } = useStimulantBucket(currentState)
  const isInPersonRequiredState =
    bucket === 'inPersonOffered' || bucket === 'inPersonNotOffered'

  const counselingTypeIdInQuery = _pick(query, 'counselingType[]')[
    'counselingType[]'
  ]
  const counselingTypeId = _isArray(counselingTypeIdInQuery)
    ? counselingTypeIdInQuery[0]
    : counselingTypeIdInQuery
  const selectedCounselingTypeFilter = groupedFilters[
    ProviderFilterTypes.COUNSELING_TYPE
  ]?.find((ct) => ct.id === counselingTypeId)
  const isPsychiatryCounselingType =
    selectedCounselingTypeFilter?.value === CounselingTypeEnum.MEDICATION

  const [
    hasUserDismissedPsychModal,
    setHasUserDismissedPsychModal,
  ] = useState<boolean>(false)

  useEffect(() => {
    setHasUserDismissedPsychModal(
      sessionStorage.getItem(
        sessionStorageKeys.PSYCH_STIMULANT_MODAL_DISMISSED
      ) === 'true'
    )
  }, [])

  // Show psych modal only when counseling type is Medication/Psychiatry (not Talk Therapy), state requires in-person for stimulants, ADHD is selected, and user has not dismissed it this session
  const showPsychModal =
    isInPersonRequiredState &&
    isShowPsychModalTreatmentON &&
    !showDropdown &&
    isAdhdFilterSelected &&
    isPsychiatryCounselingType &&
    !hasUserDismissedPsychModal

  useEffect(() => {
    const areasIdsInQuery = _pick(query, 'areaOfConcern[]')['areaOfConcern[]']

    if (!areasIdsInQuery && !appliedAreasOfConcern.length) {
      return
    }

    if (!areasIdsInQuery) {
      //When Clear button in pill was clicked
      setAppliedAreasOfConcerns([])
      return
    }

    if (!_isArray(areasIdsInQuery)) {
      //In case only one Area of Concern was Selected
      setAppliedAreasOfConcerns([areasIdsInQuery as string])
    } else {
      setAppliedAreasOfConcerns([...(areasIdsInQuery as string[])])
    }
  }, [query])

  useEffect(() => {
    if (showPsychModal) {
      setShowPsychNoticeModal(true)
    }
  }, [showPsychModal])

  const onApply = (selectedAreas: string[]) => {
    onFilterSelect({ areaOfConcern: selectedAreas })
    setAppliedAreasOfConcerns(selectedAreas)
  }

  const onHidePsychNoticeModal = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(
        sessionStorageKeys.PSYCH_STIMULANT_MODAL_DISMISSED,
        'true'
      )
    }

    setHasUserDismissedPsychModal(true)
    setShowPsychNoticeModal(false)
  }

  const areaOfConcernStringValue = useMemo(() => {
    return appliedAreasOfConcern.reduce((concat, areaOfConcernId, index) => {
      const areaOfConcernFilter = allAreasOfConcern.find(
        (areaOfConcern) => areaOfConcern.id === areaOfConcernId
      )

      if (areaOfConcernFilter) {
        if (index === 0) {
          return areaOfConcernFilter.value
        }

        return `${concat}, ${areaOfConcernFilter.value}`
      }

      return concat
    }, '')
  }, [appliedAreasOfConcern, allAreasOfConcern])

  return (
    <>
      <HeaderFilterButton
        title={filterTitle.SPECIALTY}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        filterType={ProviderFilterTypes.AREA_OF_CONCERN}
        text={areaOfConcernStringValue}
        placeHolder="I want help for..."
        mobilePlaceHolder={filterTitle.SPECIALTY}
        buttonClassName={styles.helpfor_filter_button}
        containerClassName={containerClassName}
      >
        <MultiselectDropdown
          filterTypeText="specialties"
          filterList={allAreasOfConcern}
          appliedFilters={appliedAreasOfConcern}
          onApply={onApply}
          containerClassName="top-6 mt-5"
        />
      </HeaderFilterButton>
      {showPsychNoticeModal && (
        <PsychNoticeModal
          showModal={showPsychNoticeModal}
          onHide={onHidePsychNoticeModal}
          isSupportUser={isSupportOrThirdPartyUser}
          bucket={bucket}
        />
      )}
    </>
  )
}

export default AreaOfConcernFilter
